<template>
  <AuthenticatedView
    :loading="userLoading && !userResult"
    :current-user="user"
    :has-result="!!userResult"
  >
    <AppHeader
      v-if="user"
      :user="user"
      :chat-id="chatId"
      :from-id="fromId"
      dashboard-url="/dashboard"
      profile-url="/profile"
    />
    <div class="card">
      <breadcrumbs :items="breadcrumbsItems" />
      <div class="setup-header">
        <h2>{{ s__('Setup|Step-by-Step Setup') }}</h2>
        <div v-if="!isWelcomeStep" class="progress-indicator">
          {{
            sprintf(s__('Setup|Step %{current} of %{total}'), {
              current: progressCurrentStep,
              total: setupStepsCount,
            })
          }}
        </div>
      </div>

      <div v-if="loading" class="muted">{{ __('Loading…') }}</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="user && componentReady" :key="localeKey">
        <div class="step-content" ref="stepContentRef">
          <!-- Step 1: Welcome -->
          <div v-if="currentStep === 0" :key="`step-0-${localeKey}`" class="setup-welcome-step">
            <h3>{{ s__('Setup|Welcome to Tracktor!') }}</h3>
            <p class="setup-welcome-text">
              {{
                s__(
                  'Setup|Let’s quickly finish your profile in a couple of steps so your workspace and notifications are set up correctly.'
                )
              }}
            </p>
          </div>

          <!-- Step 2: Language & Timezone -->
          <SetupLanguageTimezone
            v-if="currentStep === 1"
            :key="`step-1-${localeKey}`"
            ref="stepRef"
            :user="user"
            :timezones="timezones"
            :detected-timezone="detectedTimezone"
            :saving="saving"
            @save="handleStepSave"
          />

          <!-- Step 3: Name and Username -->
          <SetupName
            v-if="currentStep === 2"
            :key="`step-2-${localeKey}`"
            ref="stepRef"
            :user="user"
            :saving="saving"
            @save="handleStepSave"
          />
        </div>

        <div v-if="currentStep < totalSteps" class="step-navigation">
          <button
            v-if="currentStep > 0"
            type="button"
            class="btn-secondary"
            :disabled="saving"
            @click="goToPreviousStep"
          >
            {{ __('Back') }}
          </button>
          <div class="spacer"></div>
          <template v-if="hasUnsavedChanges">
            <button
              type="button"
              class="btn-secondary"
              :disabled="saving"
              @click="handleResetChanges"
            >
              {{ s__('Setup|Reset') }}
            </button>
            <button
              type="button"
              class="btn-primary"
              :disabled="saving"
              @click="handleSaveAndContinue"
            >
              {{ saving ? s__('Setup|Saving...') : s__('Setup|Save') }}
            </button>
          </template>
          <template v-else>
            <button
              type="button"
              class="btn-primary"
              :disabled="saving || !canProceedWithoutSaving"
              @click="handleNext"
            >
              {{ __('Next') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </AuthenticatedView>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, sprintf, loadTranslations } from '@/locale'
import Breadcrumbs from '../common/Breadcrumbs.vue'
import SetupName from './SetupName.vue'
import SetupLanguageTimezone from './SetupLanguageTimezone.vue'
import AppHeader from '../common/AppHeader.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'

const props = defineProps({ chatId: String, fromId: String })

const loading = ref(true)
const error = ref('')
const user = ref(null)
const userId = ref(null)
const saving = ref(false)
const currentStep = ref(0)
const totalSteps = 3
const setupStepsCount = 2
const loadingTimezones = ref(true)
const timezones = ref([])
const detectedTimezone = ref('')
const stepRef = ref(null)
const stepContentRef = ref(null)
const localeKey = ref(0) // Reactive key to force re-render on language change
const hasUnsavedChanges = ref(false)
const isWelcomeStep = computed(() => currentStep.value === 0)
const progressCurrentStep = computed(() => Math.max(1, currentStep.value))
const canProceedWithoutSaving = computed(() => {
  if (isWelcomeStep.value) return true
  if (hasUnsavedChanges.value || !stepRef.value) return false
  if (typeof stepRef.value.isComplete !== 'function') return true
  return stepRef.value.isComplete()
})
const componentReady = ref(false) // Flag to prevent rendering until ready

// Get viewer's Telegram user ID - make it reactive
const viewerUid = computed(() => {
  const tg = window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initDataUnsafe
  return (tg && tg.user && tg.user.id) || props.fromId || null
})

const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const breadcrumbsItems = computed(() => {
  // Depend on localeKey to force re-render when translations load
  localeKey.value // eslint-disable-line no-unused-expressions
  return [{ label: s__('Dashboard|Main'), url: dashboardUrl.value }, { label: s__('Setup|Setup') }]
})

// GraphQL queries
const USER_Q_TELEGRAM = gql`
  query GetUserForSetupByTelegram($uid: BigInt!) {
    userByTelegram(telegramUserId: $uid) {
      id
      name
      username
      dmNotificationsEnabled
      notifyOwnActions
      notifyApproachingDeadline
      deadlineReportTime
      deadlineReportDays
      silentHours
      notificationPreferences
      compactTaskForm
      preferredLanguage
      preferredTimezone
      personalLabelsEnabled
      boardsEnabled
      templatesEnabled
    }
  }
`

const USER_Q_CURRENT = gql`
  query GetCurrentUserForSetup {
    currentUser {
      id
      name
      username
      dmNotificationsEnabled
      notifyOwnActions
      notifyApproachingDeadline
      deadlineReportTime
      deadlineReportDays
      silentHours
      notificationPreferences
      compactTaskForm
      preferredLanguage
      preferredTimezone
      personalLabelsEnabled
      boardsEnabled
      templatesEnabled
    }
  }
`

const TIMEZONES_Q = gql`
  query GetTimezones {
    timezoneOptions {
      value
      label
    }
  }
`

// Fetch timezones
const { result: timezonesResult, loading: tzLoading } = useQuery(TIMEZONES_Q)

watch(
  tzLoading,
  val => {
    loadingTimezones.value = val
  },
  { immediate: true }
)

watch(
  timezonesResult,
  data => {
    if (data?.timezoneOptions) {
      timezones.value = data.timezoneOptions
    }
  },
  { immediate: true }
)

// Detect browser timezone
if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
  detectedTimezone.value = Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Use currentUser query when there's no Telegram context (browser authentication)
// Otherwise use userByTelegram query
const useCurrentUserQuery = computed(() => !viewerUid.value)

// Fetch user data - use appropriate query based on context
const {
  result: userResultTelegram,
  loading: userLoadingTelegram,
  error: userErrorTelegram,
  refetch: refetchUserTelegram,
} = useQuery(USER_Q_TELEGRAM, () => ({ uid: Number(viewerUid.value) }), {
  enabled: computed(() => !!viewerUid.value),
  skip: useCurrentUserQuery.value,
  fetchPolicy: 'network-only',
})

const {
  result: userResultCurrent,
  loading: userLoadingCurrent,
  error: userErrorCurrent,
  refetch: refetchUserCurrent,
} = useQuery(
  USER_Q_CURRENT,
  {},
  {
    enabled: useCurrentUserQuery,
    skip: !useCurrentUserQuery.value,
    fetchPolicy: 'network-only',
  }
)

// Combine results - use whichever query is active
const userResult = computed(() =>
  useCurrentUserQuery.value ? userResultCurrent.value : userResultTelegram.value
)
const userLoading = computed(() =>
  useCurrentUserQuery.value ? userLoadingCurrent.value : userLoadingTelegram.value
)
const userError = computed(() =>
  useCurrentUserQuery.value ? userErrorCurrent.value : userErrorTelegram.value
)

// Helper function to refetch user data
const refetchUser = async () => {
  if (useCurrentUserQuery.value) {
    await refetchUserCurrent()
  } else {
    await refetchUserTelegram({ uid: Number(viewerUid.value) })
  }
}

watch(
  userLoading,
  val => {
    loading.value = val
  },
  { immediate: true }
)

// Remove HTML loading screen when data is loaded
watch(loading, isLoading => {
  if (!isLoading) {
    removeInitialLoading()
  }
})

watch(
  userError,
  val => {
    if (val) error.value = val.message || 'Failed to load user'
  },
  { immediate: true }
)

watch(
  userResult,
  data => {
    try {
      // Handle both query types: userByTelegram returns { userByTelegram }, currentUser returns { currentUser }
      const userData = data?.userByTelegram || data?.currentUser
      if (userData) {
        user.value = userData
        userId.value = userData.id

        // Update locale if user has preferred language
        if (userData.preferredLanguage) {
          document.documentElement.lang = userData.preferredLanguage
          loadTranslations(userData.preferredLanguage)
          localeKey.value++ // Force re-render
        }

        // Set component ready flag - Vue will process this reactively
        componentReady.value = false
        nextTick(() => {
          componentReady.value = true
        })
      } else if (data && !userData) {
        // User not found - show error
        error.value = 'User not found'
        loading.value = false
        componentReady.value = false
      }
    } catch (e) {
      error.value = 'Failed to process user data'
      loading.value = false
      componentReady.value = false
    }
  },
  { immediate: true }
)

function goToPreviousStep() {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

async function handleStepSave(stepData) {
  // If language was changed, update locale immediately
  if (stepData.preferredLanguage !== undefined) {
    const newLanguage = stepData.preferredLanguage
    if (newLanguage) {
      document.documentElement.lang = newLanguage
      loadTranslations(newLanguage)
    } else {
      // Auto-detect: use browser/Telegram language
      const detectedLocale = document.documentElement.lang || 'en'
      loadTranslations(detectedLocale)
    }
    localeKey.value++ // Force re-render with new translations
  }

  // Step saved successfully, advance to next step
  if (currentStep.value < totalSteps - 1) {
    currentStep.value++
  }
}

// Function to check for changes in current step
function checkForChanges() {
  // Only check if user is loaded and stepRef exists
  if (!user.value || !stepRef.value) {
    hasUnsavedChanges.value = false
    return
  }

  // Check if hasChanges method exists
  if (typeof stepRef.value.hasChanges !== 'function') {
    hasUnsavedChanges.value = false
    return
  }

  try {
    hasUnsavedChanges.value = stepRef.value.hasChanges()
  } catch (e) {
    // If accessing hasChanges fails, assume no changes
    hasUnsavedChanges.value = false
  }
}

// Periodically check for changes (in case form fields change)
// This is needed because we can't directly watch form fields in child components
let changeCheckInterval = null

// Setup change checking for current step
function setupChangeChecking() {
  // Clear any existing interval
  if (changeCheckInterval) {
    clearInterval(changeCheckInterval)
    changeCheckInterval = null
  }

  // Only set up if user is loaded and step content is in DOM
  if (!user.value || !stepContentRef.value) {
    hasUnsavedChanges.value = false
    return
  }

  // Wait for Vue to update the DOM and mount the new step component
  // Use nextTick to ensure component is mounted, then check again after Vue processes
  nextTick(() => {
    // Double-check that ref is available, component is in DOM, and component is ready
    const ref = stepRef.value
    const contentEl = stepContentRef.value
    if (ref && contentEl && typeof ref === 'object' && typeof ref.hasChanges === 'function') {
      try {
        // Do an immediate check
        checkForChanges()

        // Also check again after Vue has fully processed (double nextTick for safety)
        nextTick(() => {
          checkForChanges()
        })

        // Set up interval to check periodically
        changeCheckInterval = setInterval(() => {
          // Guard against ref being null, content not in DOM, or user being unloaded
          const currentRef = stepRef.value
          const currentContent = stepContentRef.value
          if (
            !user.value ||
            !currentContent ||
            !currentRef ||
            typeof currentRef !== 'object' ||
            typeof currentRef.hasChanges !== 'function'
          ) {
            if (changeCheckInterval) {
              clearInterval(changeCheckInterval)
              changeCheckInterval = null
            }
            hasUnsavedChanges.value = false
            return
          }
          try {
            checkForChanges()
          } catch (e) {
            // If check fails, clear interval
            if (changeCheckInterval) {
              clearInterval(changeCheckInterval)
              changeCheckInterval = null
            }
            hasUnsavedChanges.value = false
          }
        }, 50) // Check every 50ms for more responsive detection
      } catch (e) {
        // If initial check fails, don't set up interval
        hasUnsavedChanges.value = false
      }
    } else {
      hasUnsavedChanges.value = false
    }
  })
}

// Watch currentStep to reset change checking
watch(
  currentStep,
  () => {
    // Reset component ready flag when step changes
    componentReady.value = false

    // Reset changes when step changes
    hasUnsavedChanges.value = false

    // Clear any existing interval
    if (changeCheckInterval) {
      clearInterval(changeCheckInterval)
      changeCheckInterval = null
    }

    // Set component ready - Vue will handle the unmount/mount cycle
    if (user.value) {
      nextTick(() => {
        componentReady.value = true
        // Set up change checking after component is ready
        nextTick(() => {
          setupChangeChecking()
        })
      })
    }
  },
  { immediate: false } // Don't run immediately
)

// Watch user to set up change checking when user loads
watch(
  () => user.value,
  () => {
    if (user.value) {
      // User just loaded, set up change checking for current step
      nextTick(() => {
        setupChangeChecking()
      })
    } else {
      // User unloaded, clear interval
      if (changeCheckInterval) {
        clearInterval(changeCheckInterval)
        changeCheckInterval = null
      }
      hasUnsavedChanges.value = false
    }
  },
  { immediate: false } // Don't run immediately
)

// Watch componentReady to trigger change checking when component becomes ready
watch(componentReady, isReady => {
  if (isReady && user.value) {
    // Component is ready, set up change checking
    nextTick(() => {
      setupChangeChecking()
    })
  }
})

// Clean up interval on unmount
onBeforeUnmount(() => {
  if (changeCheckInterval) {
    clearInterval(changeCheckInterval)
    changeCheckInterval = null
  }
})

async function handleNext() {
  if (saving.value || !canProceedWithoutSaving.value) return

  if (currentStep.value < totalSteps - 1) {
    currentStep.value++
    return
  }

  finishSetup()
}

async function handleSaveAndContinue() {
  if (!stepRef.value || saving.value) return

  saving.value = true
  error.value = ''

  try {
    // Call save method on current step component
    const success = await stepRef.value.save()
    if (success) {
      hasUnsavedChanges.value = false
      // Advance to next step after successful save
      if (currentStep.value < totalSteps - 1) {
        currentStep.value++
      } else {
        finishSetup()
      }
    }
  } catch (err) {
    error.value = err.message || 'Failed to save'
  } finally {
    saving.value = false
  }
}

function handleResetChanges() {
  if (!stepRef.value || typeof stepRef.value.reset !== 'function') return

  stepRef.value.reset()
  hasUnsavedChanges.value = false
}

function finishSetup() {
  window.location.href = dashboardUrl.value
}

// Expose refetch for step components
defineExpose({
  refetchUser,
})
</script>

<style scoped>
.setup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.setup-header h2 {
  margin: 0;
}

.progress-indicator {
  color: #666;
  font-size: 14px;
}

.step-content {
  min-height: 400px;
  margin-bottom: 24px;
}

.setup-welcome-step {
  max-width: 640px;
}

.setup-welcome-step h3 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 22px;
  color: #333;
}

.setup-welcome-text {
  margin: 0;
  color: #666;
  font-size: 15px;
  line-height: 1.5;
}

.step-navigation {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-top: 24px;
  border-top: 1px solid #eee;
}

.spacer {
  flex: 1;
}

/* Button styles inherit from forms.css */

.error {
  color: #d32f2f;
  font-size: 14px;
}

.muted {
  color: #999;
  font-size: 14px;
}
</style>
